import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import AppDatePicker from '../../../app/components/AppDatePicker';
import dayjs from 'dayjs';

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (examDate: string, sendEmail: boolean) => Promise<void>;
    serviceName: string;
    userEmail: string;
    hasScans: boolean; // czy zamówienie ma skany - wpływa na treść e-maila
    defaultDate?: Date | null; // preposedDateTime z zamówienia
}

interface FormValues {
    examDate: Date | null;
}

export default function ExamApprovalConfirmDialog({
    open,
    onClose,
    onConfirm,
    serviceName,
    userEmail,
    hasScans,
    defaultDate
}: Props) {
    const [sendEmail, setSendEmail] = useState(true);
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            examDate: defaultDate || null
        }
    });

    const handleConfirm = async (formData: FormValues) => {
        if (!formData.examDate) return;
        
        setLoading(true);
        try {
            // Dodaj 2 godziny do daty (korekta strefy czasowej)
            const adjustedExamDate = dayjs(formData.examDate).add(2, 'hour').toISOString();
            await onConfirm(adjustedExamDate, sendEmail);
        } finally {
            setLoading(false);
            setSendEmail(true); // Reset do domyślnej wartości
        }
    };

    const handleClose = () => {
        if (!loading) {
            setSendEmail(true); // Reset do domyślnej wartości
            reset({ examDate: defaultDate || null });
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="exam-approval-dialog-title"
            aria-describedby="exam-approval-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="exam-approval-dialog-title">
                {hasScans ? 'Zatwierdzenie egzaminu' : 'Zatwierdzenie egzaminu (bez skanów)'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="exam-approval-dialog-description" component="div">
                    <p>
                        Czy na pewno chcesz <strong>zatwierdzić egzamin</strong> dla zamówienia
                        na usługę <strong>"{serviceName}"</strong>?
                    </p>
                    <p>
                        Klient: <strong>{userEmail}</strong>
                    </p>
                    
                    {!hasScans && (
                        <Typography color="warning.main" sx={{ mt: 1 }}>
                            ⚠️ Zamówienie nie zawiera skanów rozwiązanych arkuszy egzaminacyjnych.
                            Klient zostanie poinformowany o konieczności ich przesłania.
                        </Typography>
                    )}
                    
                    {hasScans && (
                        <Typography color="success.main" sx={{ mt: 1 }}>
                            ✅ Certyfikaty będą dostępne do pobrania przez klienta.
                        </Typography>
                    )}
                </DialogContentText>

                <Box sx={{ mt: 3 }}>
                    <form id="exam-approval-form" onSubmit={handleSubmit(handleConfirm)}>
                        <AppDatePicker
                            label="Data egzaminu"
                            disabled={loading}
                            name="examDate"
                            control={control}
                            rules={{ required: 'Data egzaminu jest wymagana' }}
                        />
                    </form>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            hasScans 
                                ? "Wyślij e-mail z informacją o dostępności certyfikatów"
                                : "Wyślij e-mail z informacją o zatwierdzeniu i konieczności przesłania skanów"
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit" disabled={loading}>
                    Anuluj
                </Button>
                <LoadingButton
                    type="submit"
                    form="exam-approval-form"
                    color="success"
                    loading={loading}
                    autoFocus
                >
                    Zatwierdź egzamin
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
